import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import ContentCalendar from "@/pages/ContentCalendar";
import ScriptEditor from "@/pages/ScriptEditor";
import MediaGenerator from "@/pages/MediaGenerator";

// Icons
import { 
  LayoutDashboard, 
  User, 
  Calendar, 
  FileText, 
  Wand2,
  Menu,
  X,
  Sun,
  Moon
} from "lucide-react";

// UI Components
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
export const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Navigation items
const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/profile", label: "Profile", icon: User },
  { path: "/calendar", label: "Calendar", icon: Calendar },
  { path: "/scripts", label: "Scripts", icon: FileText },
  { path: "/generate", label: "Generate", icon: Wand2 },
];

// Navigation Component
const Navigation = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    // Check for saved preference or system preference
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const NavLinks = ({ onClick }) => (
    <>
      {navItems.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              isActive
                ? "bg-accent/20 text-accent font-medium"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`
          }
          data-testid={`nav-${item.label.toLowerCase()}`}
        >
          <item.icon className="w-5 h-5" />
          <span className="font-sans">{item.label}</span>
        </NavLink>
      ))}
    </>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4 md:px-6 max-w-[1600px] mx-auto">
        {/* Logo */}
        <NavLink to="/" className="flex items-center gap-3" data-testid="logo">
          <div className="w-10 h-10 rounded-full gold-gradient flex items-center justify-center">
            <span className="font-accent text-lg font-bold text-white">א</span>
          </div>
          <div className="hidden sm:block">
            <h1 className="font-display text-lg font-semibold tracking-tight">Abraham</h1>
            <p className="text-xs text-muted-foreground font-sans">Digital Covenant</p>
          </div>
        </NavLink>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `nav-link flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                  isActive
                    ? "text-accent font-medium active"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
              data-testid={`nav-desktop-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="text-sm font-sans">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDarkMode}
            className="rounded-full"
            data-testid="theme-toggle"
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72" data-testid="mobile-menu">
              <div className="flex flex-col gap-2 mt-8">
                <NavLinks onClick={() => setMobileOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

// Main App Component
function App() {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Initialize the app
    const initApp = async () => {
      try {
        await api.post('/initialize');
        setInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        // Still set initialized to true to allow the app to load
        setInitialized(true);
      }
    };

    initApp();
  }, []);

  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <Navigation />
        <main className="container mx-auto px-4 py-6 md:px-6 md:py-8 max-w-[1600px]">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/calendar" element={<ContentCalendar />} />
            <Route path="/scripts" element={<ScriptEditor />} />
            <Route path="/scripts/:dayNumber" element={<ScriptEditor />} />
            <Route path="/generate" element={<MediaGenerator />} />
          </Routes>
        </main>
        <Toaster 
          position="bottom-right" 
          richColors 
          closeButton
          toastOptions={{
            className: 'font-sans',
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
